import React from 'react';

import { format, isValid, parseISO } from 'date-fns';
import { Lock, LockOpen } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { Apollo } from 'src/gql/generated';
import type { DeepPartial } from 'ts-essentials';

import type { ReportRowQuestionJob } from '../types';

export type JobsLockState = 'all' | 'empty' | 'none' | 'partial';
export type LockAction = 'lock' | 'unlock';

interface LockIconProps {
  /**
   * Action the icon represents. 'lock' → closed lock (the user can lock more
   * cells); anything else (incl. 'unlock' and the empty/disabled affordance)
   * → open lock.
   */
  action: LockAction | 'empty';
  iconProps?: LucideProps;
}

export function LockIcon({ action, iconProps }: LockIconProps) {
  if (action === 'lock') return <Lock {...iconProps} />;
  return <LockOpen {...iconProps} />;
}

export interface LockOwner {
  displayName?: string | null;
}

export interface LockableJob {
  lockedAt?: string | null;
  lockedBy?: LockOwner | null;
}

export function getIsReportRowQuestionJob(val: unknown): val is DeepPartial<ReportRowQuestionJob> {
  const jobTypename: ReportRowQuestionJob['__typename'] = 'ExtractionJob';
  return (
    val !== null && typeof val === 'object' && '__typename' in val && val.__typename === jobTypename
  );
}

export function getIsJobLocked(
  job: LockableJob | null | undefined
): job is LockableJob & { lockedBy: LockOwner } {
  return Boolean(job?.lockedBy);
}

/**
 * Aggregates lock state across a set of candidate jobs. Callers gather the jobs
 * (typically the user's selected rows) and pass them in; the helper filters with
 * the shared `getIsReportRowQuestionJob` guard. Column-level lock state must be
 * resolved server-side via `Report.questionLockStatus` since the grid only holds
 * a virtualized slice of rows.
 */
export function getJobsLockState(jobs: unknown[]): {
  extractionJobIds: string[];
  state: JobsLockState;
} {
  const extractionJobIds: string[] = [];
  let jobCount = 0;
  let lockedCount = 0;

  for (const job of jobs) {
    if (!getIsReportRowQuestionJob(job)) continue;
    if (typeof job.id !== 'string') continue;
    extractionJobIds.push(job.id);
    jobCount++;
    if (getIsJobLocked(job)) lockedCount++;
  }

  if (jobCount === 0) return { extractionJobIds, state: 'empty' };
  if (lockedCount === 0) return { extractionJobIds, state: 'none' };
  if (lockedCount === jobCount) return { extractionJobIds, state: 'all' };
  return { extractionJobIds, state: 'partial' };
}

interface HasQuestionJobsByQuestionId {
  questionJobsByQuestionId?: Record<string, unknown> | null;
}

function getHasQuestionJobsByQuestionId(row: unknown): row is HasQuestionJobsByQuestionId {
  return row !== null && typeof row === 'object' && 'questionJobsByQuestionId' in row;
}

/**
 * Collect the candidate jobs from a set of grid rows. When `field` is supplied,
 * returns the single job at that field per row (column-lock case); otherwise
 * returns every job across every field (row-lock case).
 */
export function collectRowQuestionJobs(rows: Iterable<unknown>, field?: string): unknown[] {
  return [...rows].flatMap(row => {
    if (!getHasQuestionJobsByQuestionId(row)) return [];

    const map = row.questionJobsByQuestionId;
    if (!map || typeof map !== 'object') return [];
    if (field) return [map[field]];

    return Object.values(map);
  });
}

/**
 * Single action to surface for a given aggregate state. The UI never shows
 * both Lock and Unlock at once — partial selections offer Lock so the user
 * can protect remaining unlocked cells in one click. To unlock part of a
 * mixed selection, drill into individual cells.
 *
 * - `all` (every cell locked) → Unlock.
 * - `empty` (no cells exist in scope) → no action.
 * - `none` (cells exist, none locked) → Lock.
 * - `partial` (some locked, some not) → Lock.
 */
const LOCK_ACTION_BY_STATE = {
  all: 'unlock',
  empty: null,
  none: 'lock',
  partial: 'lock',
} satisfies Record<JobsLockState, LockAction | null>;

type LockActionInput = JobsLockState | LockableJob | null | undefined;

export function getLockAction(input: LockActionInput): LockAction | null {
  if (!input) return null;
  if (typeof input === 'string') return LOCK_ACTION_BY_STATE[input];
  return getIsJobLocked(input) ? 'unlock' : 'lock';
}

export function getLockedByTooltip(
  job: LockableJob | null | undefined,
  fallback = 'Answer locked'
): string {
  if (!getIsJobLocked(job)) return fallback;

  const lockedBy = job.lockedBy.displayName || 'a user';
  if (!job.lockedAt) return `Locked by ${lockedBy}`;

  const lockedAt = parseISO(job.lockedAt);
  if (!isValid(lockedAt)) return `Locked by ${lockedBy}`;

  return `Locked by ${lockedBy} on ${format(lockedAt, 'MMM d, yyyy')}`;
}

export const COLUMN_LOCK_LABEL = {
  lock: 'Lock column',
  unlock: 'Unlock column',
} satisfies Record<LockAction, string>;

export const COLUMN_LOCK_EMPTY_LABEL = 'No cells to lock';

export const ROWS_LOCK_LABEL = {
  lock: 'Lock rows',
  unlock: 'Unlock rows',
} satisfies Record<LockAction, string>;

const LOCK_STATE_BY_GQL = {
  [Apollo.QuestionLockState.All]: 'all',
  [Apollo.QuestionLockState.Empty]: 'empty',
  [Apollo.QuestionLockState.None]: 'none',
  [Apollo.QuestionLockState.Partial]: 'partial',
} satisfies Record<Apollo.QuestionLockState, JobsLockState>;

/**
 * Server-derived column lock state. Uses `Report.questionLockStatus`, which counts
 * locked vs total ExtractionJobs across the report's full document scope — the
 * client cannot do this aggregation reliably because the DataGrid only loads a
 * virtualized slice of rows.
 */
export function useColumnLockState(
  reportId: string | null,
  questionId: string,
  options: Omit<
    NonNullable<Parameters<typeof Apollo.useGetQuestionLockStatusQuery>[0]>,
    'variables'
  > & {
    /**
     * Live grid filterModel. When set, the resolver scopes lockedCount /
     * totalCount to the rows the user is actually looking at instead of
     * the persisted `report.state.filter`. Pass null to fall back to
     * saved state (e.g. before the grid has mounted).
     */
    filterModel?: unknown;
  } = {}
): JobsLockState {
  const { filterModel, ...queryOptions } = options;
  const { data } = Apollo.useGetQuestionLockStatusQuery({
    ...queryOptions,
    skip: queryOptions.skip || !reportId,
    variables: {
      filterModel: filterModel ?? null,
      questionId,
      reportId: reportId ?? '',
    },
  });
  const status = data?.report?.questionLockStatus;
  if (!status) return 'empty';
  return LOCK_STATE_BY_GQL[status.state];
}
